import { id, Identifiable, Task, task } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { createStateReducer } from "../../utils/createStateReducer";
import { reduceAllocation } from "../allocation";
import { reduceTaskStatus } from "../taskStatus";
import {
  handleEditRequest,
  handleEditSuccess,
  handleEditFailure,
} from "./editTask";
import {
  handleMoveKanbanTaskRequest,
  handleMoveKanbanTaskSuccess,
  handleMoveKanbanTaskFailure,
} from "./moveKanbanTask";
import {
  handleAllocateResourcesRequest,
  handleAllocateResourcesSuccess,
  handleAllocateResourcesFailure,
} from "./allocateResources";
import {
  handleAssignTagRequest,
  handleAssignTagSuccess,
  handleAssignTagFailure,
} from "./assignTag";
import {
  handleRemoveTagRequest,
  handleRemoveTagSuccess,
  handleRemoveTagFailure,
} from "./removeTag";
import { handleRemoveAllocation } from "./removeAllocation";
import { handleRemoveResource } from "./removeResource";
import { handleRemoveResourceFromProject } from "./removeResourceFromProject";
import { handleRemoveTaskStatus } from "./removeTaskStatus";
import {
  handleMarkTaskAsCompletedRequest,
  handleMarkTaskAsCompletedSucceeded,
  handleMarkTaskAsCompletedFailure,
} from "./markTaskAsCompleted";
import { handleMoveListElement } from "./moveListElement";
import { handleConstrainElements } from "./constrainElement";
import { handleMilestoneEditSuccess } from "./editMilestone";
import { handleEditConstraint } from "./editConstraint";
import { handleAttachDocumentsToElement } from "./attachDocumentsToElement";
import { handleRemoveAttachedDocument } from "./removeAttachedDocument";
import { calculateAdvancement } from "../../utils/calculateAdvancement";

const initialState = { ...task, uuid: id };

function handleTransferProjectResourceAllocations(
  currentTask: Identifiable<Task>,
  {
    tasks,
    fromProjectResource,
    toProjectResource,
  }: Boundaries["transferProjectResourceAllocations"]["success"],
): Identifiable<Task> {
  if (!tasks.some((t) => t.uuid === currentTask.uuid)) return currentTask;
  return {
    ...currentTask,
    allocations: currentTask.allocations.map((allocation) =>
      allocation.resource.uuid === fromProjectResource.resource.uuid
        ? { ...allocation, resource: toProjectResource.resource }
        : allocation,
    ),
  };
}

function canRecalculateProgress({ type }: Event) {
  return (
    type === events.editTaskStatusRequested ||
    type === events.editAllocationRequested ||
    type === events.editTaskStatusSucceeded ||
    type === events.editAllocationSucceeded ||
    type === events.editTaskStatusFailed ||
    type === events.editAllocationFailed
  );
}

function propagateEvent(task = initialState, event: Event): Identifiable<Task> {
  const nextTask = {
    ...task,
    status: reduceTaskStatus(task.status, event),
    allocations: task.allocations.map((allocation) =>
      reduceAllocation(allocation, event),
    ),
  };

  return canRecalculateProgress(event)
    ? calculateAdvancement(nextTask)
    : nextTask;
}

const reduceTask = createStateReducer(
  initialState,
  {
    [events.editTaskRequested]: handleEditRequest,
    [events.editTaskSucceeded]: handleEditSuccess,
    [events.editTaskFailed]: handleEditFailure,
    [events.moveKanbanTaskRequested]: handleMoveKanbanTaskRequest,
    [events.moveKanbanTaskSucceeded]: handleMoveKanbanTaskSuccess,
    [events.moveKanbanTaskFailed]: handleMoveKanbanTaskFailure,
    [events.allocateResourcesRequested]: handleAllocateResourcesRequest,
    [events.allocateResourcesSucceeded]: handleAllocateResourcesSuccess,
    [events.allocateResourcesFailed]: handleAllocateResourcesFailure,
    [events.assignTagRequested]: handleAssignTagRequest,
    [events.assignTagSucceeded]: handleAssignTagSuccess,
    [events.assignTagFailed]: handleAssignTagFailure,
    [events.removeTagRequested]: handleRemoveTagRequest,
    [events.removeTagSucceeded]: handleRemoveTagSuccess,
    [events.removeTagFailed]: handleRemoveTagFailure,
    [events.removeAllocationSucceeded]: handleRemoveAllocation,
    [events.removeResourceFromPlanzoneSucceeded]: handleRemoveResource,
    [events.removeResourceFromProjectSucceeded]:
      handleRemoveResourceFromProject,
    [events.markTaskAsCompletedRequested]: handleMarkTaskAsCompletedRequest,
    [events.markTaskAsCompletedSucceeded]: handleMarkTaskAsCompletedSucceeded,
    [events.markTaskAsCompletedFailed]: handleMarkTaskAsCompletedFailure,
    [events.removeTaskStatusSucceeded]: handleRemoveTaskStatus,
    [events.moveListElementSucceeded]: handleMoveListElement,
    [events.constrainElementsSucceeded]: handleConstrainElements,
    [events.editConstraintSucceeded]: handleEditConstraint,
    [events.editMilestoneSucceeded]: handleMilestoneEditSuccess,
    [events.attachDocumentsToElementSucceeded]: handleAttachDocumentsToElement,
    [events.removeAttachedDocumentSucceeded]: handleRemoveAttachedDocument,
    [events.transferProjectResourceAllocationsSucceeded]:
      handleTransferProjectResourceAllocations,
  },
  propagateEvent,
);

export { reduceTask };
